import { useState } from "react";
import { Clock, Flame, Link2, Share2, Trash2 } from "lucide-react";
import { useCreateShare, useNoteShares, useRevokeShare } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { Share } from "@/api/types";
import { formatDateTime, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CopyButton } from "@/components/ui/copy-button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function shareUrl(s: Share): string | null {
  if (!s.url) return null;
  if (/^https?:/i.test(s.url)) return s.url;
  return `${window.location.origin}${s.url.startsWith("/") ? "" : "/"}${s.url}`;
}

export function ShareMenu({ noteId }: { noteId: string }) {
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<Share | null>(null);
  const create = useCreateShare(noteId);
  const { toast } = useToast();

  function make(body: { oneTime?: boolean; expiresInHours?: number }) {
    create.mutate(body, {
      onSuccess: (s) => {
        setCreated(s);
        setOpen(true);
      },
      onError: (err) => toast({ title: "Could not create link", description: errorMessage(err), tone: "error" }),
    });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={create.isPending}>
            <Share2 /> Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => make({})}>
            <Link2 /> Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => make({ oneTime: true })}>
            <Flame /> One-time link
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => make({ expiresInHours: 24 })}>
            <Clock /> Link that expires in 24h
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setCreated(null);
              setOpen(true);
            }}
          >
            Manage links…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ShareDialog noteId={noteId} open={open} onOpenChange={setOpen} created={created} />
    </>
  );
}

function describe(s: Share): string {
  const parts: string[] = [];
  if (s.oneTime) parts.push(s.usedAt ? "one-time, used" : "one-time");
  if (s.expiresAt) parts.push(new Date(s.expiresAt) < new Date() ? "expired" : `expires ${timeAgo(s.expiresAt).replace(" ago", "")}`.replace("expires just now", "expires soon"));
  if (parts.length === 0) parts.push("open link");
  return parts.join(" · ");
}

export function ShareDialog({ noteId, open, onOpenChange, created }: { noteId: string; open: boolean; onOpenChange: (o: boolean) => void; created: Share | null }) {
  const shares = useNoteShares(noteId, open);
  const revoke = useRevokeShare(noteId);
  const { toast } = useToast();
  const url = created ? shareUrl(created) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{created ? "Link created" : "Share links"}</DialogTitle>
          <DialogDescription>
            Anyone with a link can read this note without signing in. {created?.oneTime && "This one works exactly once."}
          </DialogDescription>
        </DialogHeader>

        {created && (
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate font-mono text-xs">{url ?? "(URL not returned by the API)"}</code>
              {url && <CopyButton text={url} label="Copy link" showLabel size="sm" variant="outline" />}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
              {created.oneTime && <Badge variant="warning">one-time</Badge>}
              {created.expiresAt && <Badge variant="outline">expires {formatDateTime(created.expiresAt)}</Badge>}
              {!created.oneTime && !created.expiresAt && <Badge variant="outline">no expiry</Badge>}
            </div>
          </div>
        )}

        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Existing links</div>
          {shares.isLoading && <div className="py-3 text-sm text-muted-foreground">Loading…</div>}
          {shares.isError && <div className="py-3 text-sm text-destructive">{errorMessage(shares.error)}</div>}
          {shares.data && shares.data.length === 0 && <div className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">No active links.</div>}
          {shares.data && shares.data.length > 0 && (
            <ul className="divide-y rounded-lg border">
              {shares.data
                .filter((s) => !s.revokedAt)
                .map((s) => {
                  const u = shareUrl(s);
                  return (
                    <li key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <Link2 className="size-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-mono text-xs">{u ?? `link ${s.id.slice(0, 8)}`}</div>
                        <div className="text-xs text-muted-foreground">
                          {describe(s)} · created {timeAgo(s.createdAt)}
                        </div>
                      </div>
                      {u && <CopyButton text={u} label="Copy" />}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Revoke link"
                        title="Revoke"
                        disabled={revoke.isPending}
                        onClick={() =>
                          revoke.mutate(s.id, {
                            onError: (err) => toast({ title: "Could not revoke", description: errorMessage(err), tone: "error" }),
                          })
                        }
                      >
                        <Trash2 className="text-destructive" />
                      </Button>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
